// services/backend.js
const API_BASE = (typeof window !== 'undefined' && window.RUNTIME_CONFIG?.apiBaseUrl) 
    || import.meta?.env?.VITE_API_BASE_URL 
    || 'http://localhost:3000';

// User session management
const SESSION_KEY = 'user_session';

function getUserSession() {
    const session = sessionStorage.getItem(SESSION_KEY);
    return session ? JSON.parse(session) : null;
}

function setUserSession(userData) {
    // Store complete user data: { userId, token, user: { id, username, email, role, ... } }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(userData));
}

function clearUserSession() {
    sessionStorage.removeItem(SESSION_KEY);
}

function authHeader() {
    const session = getUserSession();
    const headers = {};

    // Add JWT Bearer token for authentication
    if (session?.token) {
        headers.Authorization = `Bearer ${session.token}`;
    }

    return headers;
}

function isSuperAdmin() {
    const session = getUserSession();
    return session?.user?.role === 'superadmin';
}

async function request(path, { method = 'GET', json, headers = {}, ...rest } = {}) {
    const requestHeaders = {
        ...authHeader(),
        ...headers,
    };

    // Only set Content-Type to application/json if we're sending JSON
    // For FormData, let the browser set the correct Content-Type with boundary
    if (json) {
        requestHeaders['Content-Type'] = 'application/json';
    }

    const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: requestHeaders,
        body: json ? JSON.stringify(json) : undefined,
        ...rest,
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await res.json().catch(() => ({})) : await res.text();

    if (!res.ok) {
        const err = new Error(payload?.error || `HTTP ${res.status}`);
        err.status = res.status;
        err.payload = payload;
        throw err;
    }
    return payload;
}

export const backend = {
    // --- Auth
    auth: {
        login: async (username, password) => {
            const response = await request('/auth/login', { method: 'POST', json: { username, password } });
            // Save complete user session after successful login
            // Expected response: { userId, token, user: { id, username, email, role, ... } }
            if (response.userId && response.token) {
                setUserSession(response);
            }
            return response;
        },
        logout: () => {
            clearUserSession();
        },
        me: () => request('/auth/me'),
        getSession: getUserSession,
        isSuperAdmin: isSuperAdmin,
    },

    // --- WordPress credentials
    // Note: WordPress credentials are managed by the backend automatically
    // based on the authenticated user's JWT token. The frontend doesn't
    // need direct access to credential endpoints.

    // --- Activities
    activities: {
        create: (activity) => request('/activities', { method: 'POST', json: activity }),
        update: (id, activity) => request(`/activities/${id}`, { method: 'PUT', json: activity }),
        list: (params = {}) => {
            const qs = new URLSearchParams(params).toString();
            return request(`/activities${qs ? `?${qs}` : ''}`, { method: 'GET' });
        },
        read: (id) => request(`/activities/${id}`, { method: 'GET' }),
        remove: (id) => request(`/activities/${id}`, { method: 'DELETE' }),

        // Translation endpoints
        createOrUpdateTranslation: (sourceId, translationData, lang = 'en') =>
            request('/activities/translate', {
                method: 'POST',
                json: { sourceId, translationData, lang }
            }),
        getTranslation: (sourceId, lang = 'en') =>
            request(`/activities/${sourceId}/translation/${lang}`),
        deleteTranslation: (sourceId, lang = 'en') =>
            request(`/activities/${sourceId}/translation/${lang}`, { method: 'DELETE' }),
    },

    // --- Categories
    categories: {
        list: () => request('/categories', { method: 'GET' }),
    },

    media: {
        async upload(file, { title } = {}) {
            const form = new FormData();
            form.append('file', file);
            if (title) form.append('title', title);
            return request('/media', { method: 'POST', body: form });
        },
        async uploadMultiple(files, { titlePrefix } = {}) {
            const form = new FormData();
            files.forEach((f, i) => form.append('files', f, f.name));
            if (titlePrefix) form.append('titlePrefix', titlePrefix);
            return request('/media/bulk', { method: 'POST', body: form });
        },
    },

    // --- Users (Superadmin)
    users: {
        create: (user) => request('/users', { method: 'POST', json: user }),
        update: (id, user) => request(`/users/${id}`, { method: 'PUT', json: user }),
        list: () => request('/users', { method: 'GET' }),
        read: (id) => request(`/users/${id}`, { method: 'GET' }),
        remove: (id) => request(`/users/${id}`, { method: 'DELETE' }),
    },

};
